import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import { Observable, throwError, of, EMPTY } from 'rxjs';
import { map, retryWhen, switchMap, switchMapTo, catchError } from 'rxjs/operators';

import { OidcSecurityService } from 'angular-auth-oidc-client';

import { genericRetryStrategy } from '@sd-portal/shared/rxjs';
import {
  PermissionsService,
  PreFlightPolicy,
  PermissionValidator
} from '@sd-portal/shared/permissions';

@Injectable({
  providedIn: 'root'
})
export class PostflightStandaloneGuard {
  constructor(
    private readonly oidcService: OidcSecurityService,
    private readonly permissionsService: PermissionsService,
    private readonly router: Router
  ) {}

  canActivate(
    _next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.preflightPermissionRedirect$();
  }
  canActivateChild(
    _next: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.preflightPermissionRedirect$();
  }
  canLoad(
    _route: Route,
    _segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.checkPreflightPermissions$();
  }

  private readonly isAuth$ = () =>
    this.oidcService.isAuthenticated().pipe(
      switchMap(auth => (auth ? of(auth) : throwError('no auth'))),
      retryWhen(genericRetryStrategy()),
      catchError(() => {
        this.router.navigate(['/error'], { skipLocationChange: true });
        return EMPTY;
      })
    );

  private readonly checkPreflightPermissions$ = () =>
    this.isAuth$().pipe(switchMapTo(this.permissionsService.currentUserCan(preflightAccess)));

  private readonly preflightPermissionRedirect$ = () => {
    const urlPostflight = this.router.parseUrl('/post-flight');
    return this.checkPreflightPermissions$().pipe(map(access => access || urlPostflight));
  };
}

const preflightReadonly = [
  PreFlightPolicy.FuelVendorUploadReadonly,
  PreFlightPolicy.FuelVendorUploadFullAccess,
  PreFlightPolicy.DayCalendarFullAccess,
  PreFlightPolicy.DayCalendarReadOnly,
  PreFlightPolicy.MonthlyCalendarFullAccess,
  PreFlightPolicy.MonthlyCalendarReadOnly,
  PreFlightPolicy.StaffEventScheduleFullAccess,
  PreFlightPolicy.StaffEventScheduleReadOnly,
  PreFlightPolicy.TripBuilderFullAccess,
  PreFlightPolicy.TripBuilderReadOnly,
  PreFlightPolicy.AcknowledgementFullAccess,
  PreFlightPolicy.WeeklyCalendarFullAccess,
  PreFlightPolicy.WeeklyCalendarReadOnly
];
const preflightAccess = new PermissionValidator(preflightReadonly, false);
